import { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { FormattedNumber } from 'gatsby-plugin-react-intl';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import clip from 'text-clipper';
import he from 'he';
import AOS from 'aos';

import RichText, { fragment } from '../../../Core/RichText';
import Link from '../../../Core/Link';
import Disclaimer from '../../Disclaimer';

import {
  Section,
  Grid,
  Header,
  Headline,
  Tiles,
  TilesBackground,
  Tile,
  TileHeadline,
  TileSubhead,
  BubbleImg,
  Img,
  Footer,
} from './styles';

const headline = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
    [BLOCKS.HEADING_2]: (_, children) => <>{children}</>,
    [BLOCKS.HEADING_3]: (_, children) => <>{children}</>,
  },
};

const FeatureTiles = ({ data }) => {
  if (!data) return null;

  const {
    anchorName,
    backgroundColor,
    component,
    contentAlignment,
    sectionContainerHeading,
    sectionContainerHeadingColor,
    sectionSubheading,
    optimumStripe,
    cardNumbering,
    elements,
    ctas,
    legalCopy,
  } = data;

  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  const numTiles = elements.length;

  /**
   * * Check if `optimumStripe` is returning a value and change to a boolean value. Use this value to determine if the Optimum stripe should appear behind the cards.
   */
  const stripe = !!optimumStripe;

  /**
   * * Check if `cardNumbering` is returning a value and change to a boolean value. Use this value to determine if numbers should appear on the cards.
   */
  const numbering = !!cardNumbering;

  const header = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <Headline>{children}</Headline>,
      [BLOCKS.HEADING_2]: (_, children) => <Headline>{children}</Headline>,
    },
  };

  return (
    <Section
      id={anchorName}
      backgroundColor={backgroundColor}
      contentAlignment={contentAlignment}
      className={component}
    >
      <Grid contentColor={backgroundColor}>
        <Header
          data-aos="fade-up"
          data-aos-duration="750"
          headingColor={sectionContainerHeadingColor}
        >
          <RichText document={sectionContainerHeading} options={header} />
          <RichText document={sectionSubheading} />
        </Header>
        <Tiles numTiles={numTiles} cardNumbering={numbering}>
          {stripe === true && (
            <TilesBackground
              data-aos="fade-up"
              data-aos-delay="700"
              data-aos-duration="500"
            />
          )}
          {elements.map(
            (
              {
                id,
                headingColor,
                heading,
                subheading,
                bodyCopy,
                imageDam,
                imageToggle,
                ctas: elementCtas,
              },
              index,
            ) => {
              const tileNum = index + 1;

              return (
                <Tile
                  key={id}
                  data-aos="fade-up"
                  data-aos-delay={`${(index + 1) * 200}`}
                  data-aos-duration="1000"
                  imageToggle={imageToggle}
                  tileNum={tileNum}
                  cardNumbering={numbering}
                >
                  {subheading || (imageDam && imageToggle === false) ? (
                    <TileSubhead icon={imageDam.asset.localImage.publicURL}>
                      {component !== 'card-steps-section' && (
                        <FormattedNumber value={subheading} />
                      )}
                    </TileSubhead>
                  ) : null}
                  <TileHeadline
                    headingColor={headingColor}
                    imageToggle={imageToggle}
                  >
                    <RichText document={heading} options={headline} />
                  </TileHeadline>
                  <p>
                    {ReactHtmlParser(
                      clip(
                        he.decode(
                          documentToHtmlString(JSON.parse(bodyCopy.raw), {
                            renderNode: {
                              [BLOCKS.PARAGRAPH]: (node, next) =>
                                `${next(node.content)}`,
                            },
                            renderMark: {
                              [MARKS.BOLD]: text => `<strong>${text}</strong>`,
                              [MARKS.ITALIC]: text => `<em>${text}</em>`,
                            },
                          }),
                        ),
                        {
                          breakWords: true,
                          html: true,
                        },
                      ),
                    )}

                    {elementCtas &&
                      elementCtas.map(cta => {
                        if (cta.hideFrench === true) return null;
                        return (
                          <Link key={cta.id} data={cta}>
                            <RichText document={cta.label} options={fragment} />
                          </Link>
                        );
                      })}
                  </p>
                  {imageDam && imageToggle === true ? (
                    <BubbleImg>
                      <Img src={imageDam} alt={imageDam.altText || ''} />
                    </BubbleImg>
                  ) : null}
                </Tile>
              );
            },
          )}
        </Tiles>
        {ctas && (
          <Footer>
            {ctas &&
              ctas.map(cta => {
                if (cta.hideFrench === true) return null;
                return (
                  <Link key={cta.id} data={cta}>
                    <RichText document={cta.label} options={fragment} />
                  </Link>
                );
              })}
          </Footer>
        )}
        {legalCopy && <Disclaimer data={legalCopy} color="white" />}
      </Grid>
    </Section>
  );
};

FeatureTiles.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default FeatureTiles;
